import { useLayoutEffect, useRef, useState } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Input } from '@launchpad-ui/components';

import type { ParsedToken } from '../../../types/QueryBuilder';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useInputWidthMirror } from '../hooks/useInputWidthMirror';
import { flattenTokens, replaceToken } from '../utils';

import styles from './LogicBoolToken.module.css';

type LogicParsedToken = Extract<ParsedToken, { type: 'LOGIC_BOOL' | 'LOGIC_NOT' }>;

type ResolveFocus = { caret: 'start' | 'end' } | null;

function canonicalOperator(token: LogicParsedToken, text: string): 'AND' | 'OR' | 'NOT' | null {
  const normalized = text.trim().toUpperCase();
  if (token.type === 'LOGIC_BOOL' && (normalized === 'AND' || normalized === 'OR')) {
    return normalized;
  }
  if (token.type === 'LOGIC_NOT' && normalized === 'NOT') {
    return 'NOT';
  }
  return null;
}

export function useLogicOperatorEditing(
  tokenKey: string,
  token: LogicParsedToken,
  onChange?: (value: 'AND' | 'OR') => void,
) {
  const { activePopover, setActivePopover, dispatch, query, parsedQuery } = useQueryBuilderContext();
  const { readonly } = useFilterBarContext();
  const isEditing = !readonly && activePopover?.tokenKey === tokenKey && activePopover.part === 'operator';
  const seed = isEditing ? activePopover.draft : undefined;
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? seed ?? token.value;

  const latestRef = useRef({ value, token, query, parsedQuery });
  latestRef.current = { value, token, query, parsedQuery };
  const skipResolveRef = useRef(false);
  const resolveFocusRef = useRef<ResolveFocus>(null);
  const stoppingRef = useRef(false);
  const wasEditingRef = useRef(isEditing);

  function precedingSlotKey(): string {
    const flatIdx = parseInt(tokenKey, 10);
    if (Number.isNaN(flatIdx) || flatIdx <= 0) {
      return 'ft-start';
    }
    const previous = flattenTokens(latestRef.current.parsedQuery)[flatIdx - 1];
    return previous?.type === 'FREE_TEXT' ? String(flatIdx - 1) : `ft-after-${flatIdx - 1}`;
  }

  function deleteToken() {
    skipResolveRef.current = true;
    stoppingRef.current = true;
    dispatch({
      type: 'DELETE_TOKEN',
      token: latestRef.current.token,
      focusOverride: { itemKey: precedingSlotKey(), caret: 'end' },
    });
    setActivePopover(null);
  }

  function commitOperator(text: string) {
    const current = latestRef.current;
    if (text === current.token.value) {
      return;
    }
    dispatch({ type: 'SET_QUERY', query: replaceToken(current.query, current.token, text) });
    if (current.token.type === 'LOGIC_BOOL' && (text === 'AND' || text === 'OR')) {
      onChange?.(text);
    }
  }

  function resolveDraft(text: string, focus: ResolveFocus) {
    const current = latestRef.current;
    const trimmed = text.trim();
    if (trimmed === '') {
      dispatch({
        type: 'DELETE_TOKEN',
        token: current.token,
        ...(focus ? { focusOverride: { itemKey: precedingSlotKey(), caret: 'end' as const } } : {}),
      });
      return;
    }
    const canonical = canonicalOperator(current.token, trimmed);
    if (canonical) {
      commitOperator(canonical);
      return;
    }
    dispatch({
      type: 'SET_QUERY',
      query: replaceToken(current.query, current.token, trimmed),
      ...(focus ? { focusOverride: { itemKey: tokenKey, caret: focus.caret } } : {}),
    });
  }

  useLayoutEffect(() => {
    const wasEditing = wasEditingRef.current;
    wasEditingRef.current = isEditing;
    if (!wasEditing || isEditing) {
      return;
    }
    const skip = skipResolveRef.current;
    const focus = resolveFocusRef.current;
    skipResolveRef.current = false;
    resolveFocusRef.current = null;
    stoppingRef.current = false;
    setDraft(null);
    if (!skip) {
      resolveDraft(latestRef.current.value, focus);
    }
  }, [isEditing]);

  function handleInput(text: string) {
    setDraft(text);
    if (text === '') {
      deleteToken();
      return;
    }
    const canonical = canonicalOperator(token, text);
    if (canonical) {
      commitOperator(canonical);
    }
  }

  function startEditing() {
    if (readonly) {
      return;
    }
    setDraft(null);
    setActivePopover({ tokenKey, part: 'operator' });
  }

  function stopEditing({ revert = false, focus = null }: { revert?: boolean; focus?: ResolveFocus } = {}) {
    if (!isEditing || stoppingRef.current) {
      return;
    }
    stoppingRef.current = true;
    skipResolveRef.current = revert;
    resolveFocusRef.current = focus;
    setActivePopover(null);
  }

  return { isEditing, value, handleInput, startEditing, stopEditing };
}

interface LogicOperatorInputProps {
  tokenKey: string;
  operator: string;
  value: string;
  onInput: (text: string) => void;
  onStop: (options: { revert?: boolean; focus?: ResolveFocus }) => void;
}

export function LogicOperatorInput({ tokenKey, operator, value, onInput, onStop }: LogicOperatorInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  useInputWidthMirror(inputRef);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input && document.activeElement === input) {
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, []);

  function refocusOperatorButton() {
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(
          `[data-token-container][data-key="${CSS.escape(tokenKey)}"] button[data-part="operator"]`,
        )
        ?.focus();
    });
  }

  return (
    <Input
      ref={inputRef}
      data-part="operator"
      className={styles.input}
      aria-label={msg('queryBuilder.logicOperator.label', { operator })}
      defaultValue={value}
      autoFocus
      autoComplete="off"
      spellCheck={false}
      onInput={(e) => onInput(e.currentTarget.value)}
      onBlur={(e) => {
        const container = inputRef.current?.closest<HTMLElement>('[data-token-container]');
        const next = e.relatedTarget;
        if (next instanceof Node && container?.contains(next)) {
          return;
        }
        const barRoot = container?.closest<HTMLElement>('[role="grid"], [data-qb-nav-root]');
        if (!(next instanceof Node) || !barRoot?.contains(next)) {
          onStop({ focus: null });
          return;
        }
        const position = e.currentTarget.compareDocumentPosition(next);
        // oxlint-disable-next-line no-bitwise
        const movedLeft = !!(position & Node.DOCUMENT_POSITION_PRECEDING);
        onStop({ focus: { caret: movedLeft ? 'start' : 'end' } });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onStop({ focus: { caret: 'end' } });
          refocusOperatorButton();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onStop({ revert: true });
          refocusOperatorButton();
        }
      }}
    />
  );
}
